<template>
    <div class="nudge-manager">
        <div class="d-flex align-items-center justify-content-between mb-4">
            <h4 class="mb-0"><i class="fa fa-lightbulb-o text-success mr-2"></i>Feature Discovery Nudges</h4>
            <div>
                <b-button v-if="!allNudges || allNudges.length === 0" variant="outline-primary" class="mr-2" @click="seedDefaults" :disabled="seeding">
                    <i class="fa fa-database mr-1"></i> {{ seeding ? 'Seeding...' : 'Load Default Nudges' }}
                </b-button>
                <b-button variant="success" @click="openCreate">
                    <i class="fa fa-plus mr-1"></i> Create Nudge
                </b-button>
            </div>
        </div>

        <!-- Live Preview -->
        <div class="nudge-preview-section mb-4" v-if="previewNudge">
            <div class="d-flex align-items-center justify-content-between mb-2">
                <small class="text-muted text-uppercase" style="letter-spacing: 0.5px; font-weight: 600; font-size: 10px;">
                    <i class="fa fa-eye mr-1"></i> Live Preview
                </small>
                <small class="text-muted" v-if="previewSource">{{ previewSource }}</small>
            </div>
            <div :class="['nudge-preview-card', { 'nudge-preview-card--coming-soon': previewNudge.type === 'coming_soon' }]">
                <div class="nudge-preview-card__label">
                    <i class="fa fa-lightbulb-o"></i>
                    {{ previewNudge.type === 'coming_soon' ? 'Coming Soon' : 'Tip' }}
                </div>
                <div class="nudge-preview-card__body">
                    <div class="nudge-preview-card__icon">
                        <i :class="'fa ' + (previewNudge.icon || 'fa-lightbulb-o')"></i>
                    </div>
                    <div class="nudge-preview-card__content">
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <h4 class="nudge-preview-card__title">{{ previewNudge.title || 'Nudge Title' }}</h4>
                            <span v-if="previewNudge.type === 'coming_soon'" class="nudge-preview-badge">Coming Soon</span>
                        </div>
                        <p class="nudge-preview-card__text">{{ previewNudge.description || previewNudge.text || 'Nudge description will appear here...' }}</p>
                    </div>
                    <div class="nudge-preview-card__actions">
                        <span v-if="previewNudge.type !== 'coming_soon'" class="nudge-preview-card__cta">
                            <i :class="'fa ' + (previewNudge.icon || 'fa-lightbulb-o') + ' mr-1'"></i>
                            {{ previewNudge.ctaText || 'Button Text' }}
                        </span>
                        <span v-else class="nudge-preview-card__cta nudge-preview-card__cta--soon">
                            <i class="fa fa-clock-o mr-1"></i>
                            Stay Tuned
                        </span>
                        <span class="nudge-preview-card__dismiss">
                            <i class="fa fa-times"></i>
                        </span>
                    </div>
                </div>
                <div class="nudge-preview-card__nav" v-if="allNudges && allNudges.length > 1 && !showModal">
                    <button class="nudge-preview-card__arrow-btn" @click="previewPrev" :disabled="previewIndex === 0">
                        <i class="fa fa-chevron-left"></i>
                    </button>
                    <span class="nudge-preview-card__dots">
                        <span v-for="(n, i) in allNudges" :key="n.id || i"
                            class="nudge-preview-card__dot"
                            :class="{ 'nudge-preview-card__dot--active': i === previewIndex }"
                            @click="previewIndex = i"></span>
                    </span>
                    <button class="nudge-preview-card__arrow-btn" @click="previewNext" :disabled="previewIndex === allNudges.length - 1">
                        <i class="fa fa-chevron-right"></i>
                    </button>
                    <small class="ml-2 text-muted" style="font-size: 11px;">{{ previewIndex + 1 }} / {{ allNudges.length }}</small>
                </div>
            </div>
        </div>

        <!-- Nudges Table -->
        <b-card class="nudge-table-card">
            <div v-if="!allNudges || allNudges.length === 0" class="text-center text-muted py-4">
                No nudges created yet. Click "Create Nudge" to get started.
            </div>
            <table v-else class="table table-hover mb-0">
                <thead>
                    <tr>
                        <th>Title</th>
                        <th>Show On</th>
                        <th>Roles</th>
                        <th>Active</th>
                        <th>Priority</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="nudge in allNudges" :key="nudge.id">
                        <td>
                            <strong>{{ nudge.title }}</strong>
                            <br><small class="text-muted">{{ nudge.description | truncate }}</small>
                        </td>
                        <td>
                            <span
                                v-for="route in (nudge.showOnRoutes || [])"
                                :key="route"
                                class="route-badge mr-1 mb-1"
                            >{{ routeLabel(route) }}</span>
                        </td>
                        <td>
                            <span v-if="!nudge.targetRoles || nudge.targetRoles.length === 0" class="text-muted">All</span>
                            <span v-else>{{ nudge.targetRoles.join(', ') }}</span>
                        </td>
                        <td>
                            <label class="active-toggle mb-0">
                                <input
                                    type="checkbox"
                                    :checked="nudge.active"
                                    @change="toggleActive(nudge)"
                                />
                                <span class="toggle-slider"></span>
                            </label>
                        </td>
                        <td>{{ nudge.priority || 10 }}</td>
                        <td>
                            <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEdit(nudge)">
                                <i class="fa fa-pencil"></i>
                            </b-button>
                            <b-button size="sm" variant="outline-danger" @click="deleteNudge(nudge.id)">
                                <i class="fa fa-trash"></i>
                            </b-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </b-card>

        <!-- Create/Edit Modal -->
        <b-modal
            id="nudge-modal"
            :title="editing ? 'Edit Nudge' : 'Create Nudge'"
            size="lg"
            v-model="showModal"
            @ok.prevent="save"
            ok-title="Save"
            ok-variant="success"
        >
            <b-input-group prepend="Title" class="mb-3">
                <b-form-input v-model="form.title" placeholder="e.g., Try Kanban Board" required trim />
            </b-input-group>

            <b-input-group prepend="Description" class="mb-3">
                <b-form-textarea v-model="form.description" placeholder="Short description of the feature" rows="2" trim />
            </b-input-group>

            <b-input-group prepend="Icon" class="mb-3">
                <b-form-input v-model="form.icon" placeholder="fa-th-large" trim />
                <b-input-group-append>
                    <b-input-group-text>
                        <i :class="'fa ' + (form.icon || 'fa-lightbulb-o')" />
                    </b-input-group-text>
                </b-input-group-append>
            </b-input-group>

            <div class="mb-3">
                <label class="fw-semi-bold">Type</label>
                <b-form-select v-model="form.type" :options="TYPE_OPTIONS" />
            </div>

            <b-input-group prepend="CTA Button Text" class="mb-3">
                <b-form-input v-model="form.ctaText" :placeholder="form.type === 'coming_soon' ? 'Not needed (shows Stay Tuned)' : 'e.g., Try it now'" :disabled="form.type === 'coming_soon'" trim />
            </b-input-group>

            <div class="mb-3">
                <label class="fw-semi-bold">CTA Route</label>
                <b-form-select v-model="form.ctaRoute" :options="routeOptions">
                    <template #first>
                        <b-form-select-option :value="''" disabled>-- Select Route --</b-form-select-option>
                    </template>
                </b-form-select>
            </div>

            <div class="mb-3">
                <label class="fw-semi-bold">Show On Pages</label>
                <Multiselect
                    v-model="form.showOnRoutes"
                    :options="AVAILABLE_ROUTES"
                    :multiple="true"
                    track-by="path"
                    label="label"
                    placeholder="Select pages where this nudge appears"
                />
            </div>

            <div class="mb-3">
                <label class="fw-semi-bold">Target Roles</label>
                <Multiselect
                    v-model="form.targetRoles"
                    :options="ROLE_OPTIONS"
                    :multiple="true"
                    placeholder="Leave empty for all roles"
                />
            </div>

            <b-input-group prepend="Target Sites" class="mb-3">
                <b-form-textarea
                    v-model="form.targetSiteIDsRaw"
                    placeholder="Comma-separated site IDs (leave empty for all sites)"
                    rows="2"
                    trim
                />
            </b-input-group>

            <b-input-group prepend="Priority" class="mb-3">
                <b-form-input v-model.number="form.priority" type="number" min="1" />
            </b-input-group>

            <b-form-checkbox v-model="form.active" class="mb-2">Active</b-form-checkbox>
        </b-modal>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import { SEED_NUDGES } from '@/store/nudges-seed';

const AVAILABLE_ROUTES = [
    { path: '/app/admin', label: 'Admin Dashboard' },
    { path: '/app/todo', label: 'Tasks' },
    { path: '/app/kanban', label: 'Kanban Board' },
    { path: '/app/chat', label: 'Team Chat' },
    { path: '/app/conversations', label: 'SMS Conversations' },
    { path: '/app/campaigns', label: 'Campaigns' },
    { path: '/app/forms', label: 'Forms' },
    { path: '/app/wiki', label: 'Wiki' },
    { path: '/app/huddle', label: 'Huddle' },
    { path: '/app/team-insights', label: 'Team Insights' },
    { path: '/app/workflow2-list', label: 'Workflows' },
    { path: '/app/practice-pulse', label: 'Practice Pulse' },
    { path: '/overdue-recall', label: 'Overdue Recall' },
    { path: '/unscheduled-treatment', label: 'Unscheduled Treatment' },
    { path: '/broken-appointments', label: 'Broken Appointments' },
    { path: '/app/aging', label: 'Aging Reports' },
];

const ROLE_OPTIONS = ['Super Admin', 'Admin', 'Manager', 'Employee', 'Outsourced'];

const ROUTE_MAP = {};
AVAILABLE_ROUTES.forEach(r => { ROUTE_MAP[r.path] = r.label; });

const TYPE_OPTIONS = [
    { value: 'discovery', text: 'Feature Discovery (green)' },
    { value: 'coming_soon', text: 'Coming Soon (purple)' },
];

function emptyForm() {
    return {
        title: '',
        description: '',
        icon: '',
        ctaText: '',
        ctaRoute: '',
        type: 'discovery',
        showOnRoutes: [],
        targetRoles: [],
        targetSiteIDsRaw: '',
        priority: 10,
        active: true,
    };
}

export default {
    name: 'NudgeManager',
    components: { Multiselect },

    data() {
        return {
            showModal: false,
            editing: false,
            editingId: null,
            seeding: false,
            previewIndex: 0,
            form: emptyForm(),
            AVAILABLE_ROUTES,
            ROLE_OPTIONS,
            TYPE_OPTIONS,
        };
    },

    computed: {
        ...mapGetters('nudges', ['allNudges']),
        previewNudge() {
            if (this.showModal) return this.form;
            if (this.allNudges && this.allNudges.length > 0) {
                const idx = Math.min(this.previewIndex, this.allNudges.length - 1);
                return this.allNudges[idx];
            }
            return null;
        },
        previewSource() {
            if (this.showModal) return this.editing ? 'Editing...' : 'Creating...';
            if (this.allNudges && this.allNudges.length > 0) return `Previewing: ${this.previewNudge.title}`;
            return '';
        },
        routeOptions() {
            return AVAILABLE_ROUTES.map(r => ({ value: r.path, text: r.label }));
        },
    },

    filters: {
        truncate(val) {
            if (!val) return '';
            return val.length > 80 ? val.substring(0, 80) + '...' : val;
        },
    },

    mounted() {
        this.fetchNudges();
    },

    methods: {
        ...mapActions('nudges', ['fetchNudges', 'createNudge', 'updateNudge', 'deleteNudge_action']),

        routeLabel(path) {
            return ROUTE_MAP[path] || path;
        },

        openCreate() {
            this.form = emptyForm();
            this.editing = false;
            this.editingId = null;
            this.showModal = true;
        },

        openEdit(nudge) {
            this.editing = true;
            this.editingId = nudge.id;
            this.form = {
                title: nudge.title || '',
                // Legacy seed docs carry the body in `text`; the editor
                // canonicalized on `description` later. Fall back so
                // pre-seeded nudges open with their body pre-filled
                // (otherwise the textarea was empty and the "Description
                // is required" validator would block save).
                description: nudge.description || nudge.text || '',
                icon: nudge.icon || '',
                ctaText: nudge.ctaText || '',
                ctaRoute: nudge.ctaRoute || '',
                type: nudge.type || 'discovery',
                showOnRoutes: (nudge.showOnRoutes || []).map(p => AVAILABLE_ROUTES.find(r => r.path === p)).filter(Boolean),
                targetRoles: nudge.targetRoles || [],
                targetSiteIDsRaw: (nudge.targetSiteIDs || []).join(', '),
                priority: nudge.priority || 10,
                active: nudge.active !== false,
            };
            this.showModal = true;
        },

        async save() {
            const isComingSoon = this.form.type === 'coming_soon';
            if (!this.form.title || !this.form.description || (!isComingSoon && !this.form.ctaText)) {
                alert('Title and Description are required.' + (isComingSoon ? '' : ' CTA Button Text is also required.'));
                return;
            }
            const payload = {
                title: this.form.title,
                description: this.form.description,
                // FeatureNudge.vue reads `text` at runtime (it predates the
                // `description` field added for the editor). Mirror the
                // description into `text` on every save so edits actually
                // show up on the live banner, and so both fields stay in
                // sync for any future reader.
                text: this.form.description,
                icon: this.form.icon || 'fa-lightbulb-o',
                ctaText: isComingSoon ? 'Stay Tuned' : this.form.ctaText,
                ctaRoute: isComingSoon ? '' : this.form.ctaRoute,
                type: this.form.type || 'discovery',
                showOnRoutes: this.form.showOnRoutes.map(r => r.path),
                targetRoles: this.form.targetRoles,
                targetSiteIDs: this.form.targetSiteIDsRaw
                    ? this.form.targetSiteIDsRaw.split(',').map(s => s.trim()).filter(Boolean)
                    : [],
                priority: this.form.priority || 10,
                active: this.form.active,
            };

            if (this.editing) {
                await this.$store.dispatch('nudges/updateNudge', { id: this.editingId, ...payload });
            } else {
                await this.$store.dispatch('nudges/createNudge', payload);
            }
            this.showModal = false;
        },

        async deleteNudge(id) {
            if (!confirm('Delete this nudge?')) return;
            await this.$store.dispatch('nudges/deleteNudge', id);
        },

        async toggleActive(nudge) {
            await this.$store.dispatch('nudges/updateNudge', {
                id: nudge.id,
                active: !nudge.active,
            });
        },

        previewPrev() {
            if (this.previewIndex > 0) this.previewIndex--;
        },
        previewNext() {
            if (this.allNudges && this.previewIndex < this.allNudges.length - 1) this.previewIndex++;
        },

        async seedDefaults() {
            if (!confirm(`This will create ${SEED_NUDGES.length} default nudges. Continue?`)) return;
            this.seeding = true;
            try {
                for (const nudge of SEED_NUDGES) {
                    await this.$store.dispatch('nudges/createNudge', nudge);
                }
            } catch (err) {
                console.error('Seed failed:', err);
                alert('Error seeding nudges: ' + err.message);
            } finally {
                this.seeding = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.nudge-manager {
    .nudge-table-card {
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        box-shadow: none;
    }

    table {
        th {
            border-top: none;
            font-size: 0.85rem;
            text-transform: uppercase;
            color: #718096;
            font-weight: 600;
        }
        td {
            vertical-align: middle;
        }
    }

    .route-badge {
        display: inline-block;
        padding: 2px 8px;
        font-size: 0.75rem;
        border-radius: 12px;
        background-color: rgba(58, 191, 148, 0.12);
        color: #2d8a6a;
        white-space: nowrap;
    }

    /* Toggle switch */
    .active-toggle {
        position: relative;
        display: inline-block;
        width: 40px;
        height: 22px;
        cursor: pointer;

        input {
            opacity: 0;
            width: 0;
            height: 0;
        }

        .toggle-slider {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background-color: #cbd5e0;
            border-radius: 22px;
            transition: 0.2s;

            &::before {
                content: '';
                position: absolute;
                height: 16px;
                width: 16px;
                left: 3px;
                bottom: 3px;
                background-color: white;
                border-radius: 50%;
                transition: 0.2s;
            }
        }

        input:checked + .toggle-slider {
            background-color: #3abf94;
        }

        input:checked + .toggle-slider::before {
            transform: translateX(18px);
        }
    }

    /* Live Preview */
    .nudge-preview-section {
        padding: 16px;
        background: #f8fafc;
        border: 1px dashed #e2e8f0;
        border-radius: 12px;
    }

    .nudge-preview-card {
        background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf4 100%);
        border: 1px solid rgba(58, 191, 148, 0.2);
        border-left: 4px solid #3abf94;
        border-radius: 12px;
        overflow: hidden;
    }

    .nudge-preview-card__label {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 8px 20px 0;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        color: #2d9474;
        opacity: 0.7;
    }

    .nudge-preview-card--coming-soon .nudge-preview-card__label {
        color: #6d28d9;
    }

    .nudge-preview-card__body {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 10px 20px 16px;
    }

    .nudge-preview-card__icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #3abf94;
        font-size: 18px;
        flex-shrink: 0;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }

    .nudge-preview-card__content {
        flex: 1;
        min-width: 0;
    }

    .nudge-preview-card__title {
        font-size: 14px;
        font-weight: 600;
        color: #0f172a;
        margin: 0 0 2px;
    }

    .nudge-preview-card__text {
        font-size: 12px;
        color: #475569;
        margin: 0;
        line-height: 1.4;
    }

    .nudge-preview-card__actions {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
    }

    .nudge-preview-card__cta {
        display: inline-flex;
        align-items: center;
        padding: 8px 16px;
        background: #3abf94;
        color: white;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        cursor: default;
    }

    .nudge-preview-card__dismiss {
        color: #94a3b8;
        padding: 6px;
        border-radius: 6px;
        font-size: 14px;
        cursor: default;
    }

    .nudge-preview-card__nav {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 0 16px 12px;
    }

    .nudge-preview-card__arrow-btn {
        background: none;
        border: none;
        color: #94a3b8;
        font-size: 10px;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.15s;

        &:hover:not(:disabled) {
            color: #3abf94;
            background: rgba(58, 191, 148, 0.08);
        }

        &:disabled {
            opacity: 0.3;
            cursor: default;
        }
    }

    .nudge-preview-card__dots {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .nudge-preview-card__dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #cbd5e1;
        display: inline-block;

        &--active {
            background: #3abf94;
            width: 18px;
            border-radius: 3px;
        }
    }

    .nudge-preview-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        border-radius: 10px;
        background: #ede9fe;
        color: #7c3aed;
        white-space: nowrap;
    }

    .nudge-preview-card--coming-soon {
        background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f5f3ff 100%);
        border-color: rgba(124, 58, 237, 0.2);
        border-left-color: #7c3aed;

        .nudge-preview-card__icon {
            color: #7c3aed;
        }
    }

    .nudge-preview-card__cta--soon {
        background: #ddd6fe;
        color: #7c3aed;
    }
}
</style>
